import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";

// Detailed Brand Voice View Component
export default ({
  voice,
  handleBackToGrid,
  handleDeleteVoice,
  setSelectedVoice,
  setEditorOpen,
  setViewMode,
}: {
  voice: any;
  handleBackToGrid: (e: any) => void;
  handleDeleteVoice: Function;
  setSelectedVoice: Function;
  setEditorOpen: Function;
  setViewMode: Function;
}) => {
  if (!voice) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button onClick={handleBackToGrid}>Back to Brand Voices</Button>
        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => {
              setSelectedVoice(voice);
              setEditorOpen(true);
              setViewMode("grid");
            }}
          >
            Edit Brand Voice
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleDeleteVoice(voice.id)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {voice.name}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Industry
            </Typography>
            <Typography variant="body1">{voice.industry}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Target Audience
            </Typography>
            <Typography variant="body1">{voice.audience}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Description
            </Typography>
            <Typography variant="body1">{voice.description}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Brand Personality Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Brand Personality
        </Typography>

        {voice.brandPersonality?.traits?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Key Brand Traits
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.brandPersonality.traits.map(
                (trait: string, idx: number) => (
                  <Chip key={idx} label={trait} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.brandPersonality?.tone && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Tone of Voice
            </Typography>
            <Typography variant="body2">
              {voice.brandPersonality.tone}
            </Typography>
          </Box>
        )}

        {voice.brandPersonality?.character && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Character
            </Typography>
            <Typography variant="body2">
              {voice.brandPersonality.character}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Communication Style Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Communication Style
        </Typography>

        {voice.communicationStyle?.formality && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Level of Formality
            </Typography>
            <Typography variant="body2">
              {voice.communicationStyle.formality}
            </Typography>
          </Box>
        )}

        {voice.communicationStyle?.complexity && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Language Complexity
            </Typography>
            <Typography variant="body2">
              {voice.communicationStyle.complexity}
            </Typography>
          </Box>
        )}

        {voice.communicationStyle?.style?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Communication Style Elements
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.communicationStyle.style.map(
                (style: string, idx: number) => (
                  <Chip key={idx} label={style} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Language Guidelines Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Language Guidelines
        </Typography>

        {voice.languageGuidelines?.vocabulary?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Key Vocabulary
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.languageGuidelines.vocabulary.map(
                (word: string, idx: number) => (
                  <Chip key={idx} label={word} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.languageGuidelines?.phrases?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Phrases & Expressions
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.languageGuidelines.phrases.map(
                (phrase: string, idx: number) => (
                  <Chip key={idx} label={phrase} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.languageGuidelines?.terminology?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Industry Terminology
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.languageGuidelines.terminology.map(
                (term: string, idx: number) => (
                  <Chip key={idx} label={term} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Content Structure Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Content Structure
        </Typography>

        {voice.contentStructure?.formats?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Preferred Content Formats
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.contentStructure.formats.map(
                (format: string, idx: number) => (
                  <Chip key={idx} label={format} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.contentStructure?.organization?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Content Organization Patterns
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.contentStructure.organization.map(
                (pattern: string, idx: number) => (
                  <Chip key={idx} label={pattern} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.contentStructure?.preferences?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Structural Preferences
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.contentStructure.preferences.map(
                (preference: string, idx: number) => (
                  <Chip key={idx} label={preference} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Visual Elements Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Visual Elements
        </Typography>

        {voice.visualElements?.imagery?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Imagery Themes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.visualElements.imagery.map(
                (theme: string, idx: number) => (
                  <Chip key={idx} label={theme} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.visualElements?.colors?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Colors
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.visualElements.colors.map(
                (color: string, idx: number) => (
                  <Chip key={idx} label={color} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.visualElements?.typography && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Typography Style
            </Typography>
            <Typography variant="body2">
              {voice.visualElements.typography}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Distinctive Features Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Distinctive Brand Features
        </Typography>

        {voice.distinctiveFeatures?.unique_elements?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Unique Brand Elements
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.distinctiveFeatures.unique_elements.map(
                (element: string, idx: number) => (
                  <Chip key={idx} label={element} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.distinctiveFeatures?.taglines?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Taglines
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.distinctiveFeatures.taglines.map(
                (tagline: string, idx: number) => (
                  <Chip key={idx} label={tagline} size="small" />
                )
              )}
            </Box>
          </Box>
        )}

        {voice.distinctiveFeatures?.motifs?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Brand Motifs & Symbols
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {voice.distinctiveFeatures.motifs.map(
                (motif: string, idx: number) => (
                  <Chip key={idx} label={motif} size="small" />
                )
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Editing Instructions Section */}
      {voice.editingInstructions && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Editing Instructions
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {voice.editingInstructions}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};